import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { DismissFirstStepsAPI, GetFirstStepsAPI, SeeFirstStepAPI } from '../../../../api/API';
import { useGuidedTour } from '../../../../components/guided-tour/GuidedTourContext';
import { useRoleAccess } from '../../../../hooks/useRoleAccess';
import TOUR_SCRIPTS from '../../../../components/guided-tour/tourScripts';
import useDemoDataRemoval from '../../../../components/demo-data/useDemoDataRemoval';
import styles from './FirstStepsCard.module.css';

/**
 * "Primeiros passos" no topo do Início, para quem acabou de chegar como
 * proprietário(a) ou administrador(a); a Equipe não configura a clínica e não vê
 * o card (papeis.md, G3). Os itens, conforme o papel, e o "feito" vêm do backend
 * (FirstSteps.php); some sozinho quando tudo está feito ou quando a pessoa oculta.
 * À esquerda, o próximo passo em destaque; à direita, a lista de todos.
 * "Fazer agora" abre o guia na tela quando o passo tem roteiro (tourScripts.js);
 * item de vídeo abre a lição no Tutoriais. "demo_cleanup" abre a mesma confirmação
 * da faixa dos dados de exemplo (useDemoDataRemoval). Os ajustes de horários e
 * atendimentos (SEEN_ON_OPEN) ficam feitos ao abrir a tela pelo card.
 */
const SEEN_ON_OPEN = { hours: 'Ajustar', services: 'Conferir' };

function FirstStepsCard() {
  const navigate = useNavigate();
  const tour = useGuidedTour();
  const { canAccess } = useRoleAccess();
  const canSetUp = canAccess('firstSteps');
  const [data, setData] = useState(null);
  const demoRemoval = useDemoDataRemoval();

  useEffect(() => {
    if (!canSetUp) return;
    GetFirstStepsAPI((response) => {
      if (response?.success) setData(response);
    });
  }, [canSetUp]);

  if (!data || !data.visible) return demoRemoval.element;

  const dismiss = () => {
    setData({ ...data, visible: false });
    DismissFirstStepsAPI(() => {});
  };

  const start = (item) => {
    if (item.key === 'demo_cleanup') demoRemoval.requestRemoval();
    else if (SEEN_ON_OPEN[item.key]) {
      SeeFirstStepAPI(item.key, () => {});
      navigate(item.path);
    } else if (TOUR_SCRIPTS[item.key]) tour.start(item.key);
    else navigate(item.path);
  };
  const actionLabel = (item) => {
    if (item.key === 'setup') return 'Continuar';
    if (item.key === 'demo_cleanup') return 'Remover';
    if (SEEN_ON_OPEN[item.key]) return SEEN_ON_OPEN[item.key];
    return item.video ? 'Assistir' : 'Fazer agora';
  };

  const percent = data.total ? Math.round((data.done / data.total) * 100) : 0;
  const next = data.items.find((item) => !item.done);

  return (
    <section className={styles.card} aria-label="Primeiros passos">
      <div className={styles.hero}>
        {data.role && (
          <p className={styles.welcome}>
            Boas-vindas à {data.companyName}. Você entrou como {data.role}.
          </p>
        )}
        <div className={styles.heroTop}>
          <h2 className={styles.title}>Primeiros passos</h2>
          <span className={styles.count}>{data.done} de {data.total}</span>
        </div>
        <div className={styles.bar}><span style={{ width: `${percent}%` }} /></div>

        {next && (
          <div className={styles.next}>
            <span className={styles.nextLabel}>Próximo passo</span>
            <strong className={styles.nextTitle}>{next.label}</strong>
            <p className={styles.nextText}>{next.description}</p>
            <div className={styles.nextActions}>
              <button type="button" className={styles.primary} onClick={() => start(next)}>
                {actionLabel(next)}
                <ArrowForwardIcon fontSize="small" />
              </button>
              {!next.video && next.lesson && (
                <button
                  type="button"
                  className={styles.ghost}
                  onClick={() => navigate(`/system/tutorials?licao=${next.lesson}`)}
                >
                  <PlayArrowRoundedIcon fontSize="small" />
                  Ver o vídeo
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={styles.side}>
        <div className={styles.sideTop}>
          <span className={styles.sideHint}>Cada um leva poucos minutos</span>
          <button type="button" className={styles.close} onClick={dismiss} aria-label="Ocultar primeiros passos" title="Ocultar">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <ol className={styles.list}>
          {data.items.map((item, index) => (
            <li
              key={item.key}
              className={`${styles.item} ${item.done ? styles.done : ''} ${item === next ? styles.current : ''}`}
            >
              <span className={styles.marker}>
                {item.done ? <CheckRoundedIcon fontSize="inherit" /> : index + 1}
              </span>
              <span className={styles.label}>{item.label}</span>
              {item.done
                ? <span className={styles.doneTag}>Feito</span>
                : item !== next && (
                  <button type="button" className={styles.link} onClick={() => start(item)}>
                    {actionLabel(item)}
                  </button>
                )}
            </li>
          ))}
        </ol>

        <button type="button" className={styles.help} onClick={() => navigate('/system/tutorials')}>
          <PlayArrowRoundedIcon fontSize="small" />
          Precisa de ajuda? Ver todos os tutoriais
        </button>
      </div>
      {demoRemoval.element}
    </section>
  );
}

export default FirstStepsCard;
